// Angular + external
import {
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  NgZone,
  ViewEncapsulation,
} from "@angular/core";

export const MULTI_LINE_MIN = 1;
export const MULTI_LINE_MAX = 10;

// BFX
import { BfxBaseBlotter, provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";
// services
import { BfxInterventionBlotterService } from "./services/bfx-intervention-blotter.service";
import { TranslateService } from "@ngx-translate/core";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { BfxInterventionContextMenuService } from "./services/bfx-intervention-contextmenu.service";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterInterventionFilterService } from "../bfx-blotter-intervention/services/bfx-blotter-intervention-filter.service";
//
// form workers
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  InterventionTableDefinitionFactory,
  BfxBlotterAction,
  SystemConfigPromiseService,
  ProfilePromiseActionService,
} from "shared/cubiq-services";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";
import { GLComponentAlertStatus } from "../golden-layout/golden-layout-component";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { DITooltip } from "./renderers/DITooltip";
import { FilterConfiguration } from "../bfx-blotter-filter/FilterConfiguration";
import { Subscription } from "rxjs";
import { COLUMN_ORDER_FIXING_SOURCE_CODE_DATA } from "shared/cubiq-services/model/BmarkClientOrdersTableDefinitionFactory.constants";

@Component({
  selector: "bfx-ladder-blotter-intervention",
  templateUrl: "./html/bfx-ladder-blotter-intervention.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxInterventionBlotterComponent)],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BfxInterventionBlotterComponent extends BfxBaseBlotter {
  private lastItemCounter: number = -1;
  private viewActive: boolean = false;
  private newAlert: boolean = true;

  private subscriptions: Subscription[] = [];

  /**
   * frameworkComponents require declariation and import
   * AgGridModule.withComponents in module
   */
  public frameworkComponents: any = {
    diTooltip: DITooltip,
  };

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxInterventionBlotterService,
    _contextMenuService: BfxInterventionContextMenuService,
    zone: NgZone,
    tableDefinitionFactory: InterventionTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxBlotterInterventionFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    public goldenLayoutService: GoldenLayoutService,
    protected userProfileService: BfxUserProfileService,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );
    this.blotterId = "blotter-intervention";

    this.subscriptions.push(
      tableDefinitionFactory.$tzChange.subscribe(() => this.refreshUserSettings()),
    );
  }

  /**
   * After subscribed user settings have changed, refresh any dependent parts of the view - in this case,
   * only the fixing format can be changed
   */
  private refreshUserSettings() {
    setTimeout(() => {
      logger.out.info("BfxInterventionBlotter: Refreshing fixing col");
      this.gridOptions.api.refreshCells({
        columns: [COLUMN_ORDER_FIXING_SOURCE_CODE_DATA],
        force: true,
      });
    }, 200);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  protected initializeInMemory(): void {
    super.initializeInMemory();

    this.service.isReady().then(() => {
      this.blotterFilterService.applyFilterConfig({
        blotterId: this.blotterId,
        items: this.service.getFilterConfiguration(),
      });

      this.service.subjectNewItems.subscribe(() => {
        this.newAlert = true;
      });
    });
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.INTERVENTION;
  }

  protected initializeColumnDefinitions(): void {
    super.initializeColumnDefinitions();
    this.gridOptions.enableBrowserTooltips = false;
    // not available yet
    // this.gridOptions['tooltipShowDelay'] = 0;

    /*
        OMS-33523 option, didn't allow for dynamic update of size, so useless for DI

        // this.gridOptions.getRowClass = function (this: BfxInterventionBlotterComponent, params) {
        //   let intervention: CubiqTS.InterventionEvent = params!= null && params.data != null ? params.data : null;
        //   let multilineClass = '';
        //   if (
        //     intervention != null &&
        //     intervention.errors != null &&
        //     Array.isArray(intervention.errors.errorMessages)
        //   ) {
        //     if (
        //       intervention.errors.errorMessages.length > MULTI_LINE_MIN &&
        //       intervention.errors.errorMessages.length <= MULTI_LINE_MAX
        //     ) {
        //       multilineClass = ` intervention-data-row-multi-line intervention-data-row-multi-line-${intervention.errors.errorMessages.length}`
        //     } else if (intervention.errors.errorMessages.length > MULTI_LINE_MAX) {
        //       multilineClass = ` intervention-data-row-multi-line intervention-data-row-multi-line-${MULTI_LINE_MAX} intervention-data-row-multi-line-max`
        //     }
        //   }
        //   return 'intervention-data-row' + multilineClass;
        // }.bind(this);
        // this.gridOptions.getRowHeight = function (this: BfxInterventionBlotterComponent, params) {
        //   let intervention: CubiqTS.InterventionEvent = params!= null && params.data != null ? params.data : null;
        //   if (
        //     intervention != null &&
        //     intervention.errors != null &&
        //     Array.isArray(intervention.errors.errorMessages) 
        //   ) {
        //     if (
        //     intervention.errors.errorMessages.length > MULTI_LINE_MIN &&
        //     intervention.errors.errorMessages.length <= MULTI_LINE_MAX
        //     ) {
        //       return DEFAULT_ROW_HEIGHT * intervention.errors.errorMessages.length;
        //     } else if (
        //       intervention != null &&
        //       intervention.errors != null &&
        //       Array.isArray(intervention.errors.errorMessages) &&
        //       intervention.errors.errorMessages.length > MULTI_LINE_MAX
        //     ) {
        //       return DEFAULT_ROW_HEIGHT * MULTI_LINE_MAX;
        //     }
        //   }
        //   return DEFAULT_ROW_HEIGHT;
        // }
        */
  }

  protected checkForNoRows(): void {
    super.checkForNoRows();
    this.checkBadgeNumber();
  }

  private checkBadgeNumber() {
    if (this.gridOptions.api) {
      if (this.lastItemCounter !== this.getBadgeNumber()) {
        this.newAlert = true;
        this.lastItemCounter = this.getBadgeNumber();
        let alertStatus = this.getAlertStatus();
        this.layoutService.updateBadge(this, this.lastItemCounter, alertStatus);
      }
    }
  }

  protected gridDataUpdated(): void {
    super.gridDataUpdated();
    this.checkBadgeNumber();
  }

  protected afterFilteringCompleted(): void {
    super.afterFilteringCompleted();
    this.lastItemCounter = this.getBadgeNumber();
    let alertStatus = this.getAlertStatus();
    this.layoutService.updateBadge(this, this.lastItemCounter, alertStatus);
  }

  protected getBadgeNumber(): number {
    if (this.gridOptions && this.gridOptions.api) {
      return this.gridOptions.api.getDisplayedRowCount();
    }

    return 0;
  }

  public componentActivated(): void {
    this.viewActive = true;
    this.newAlert = false;
  }

  public componentDeactivated(): void {
    if (this.viewActive) {
      // If it was active, then ensure that new alerts are disabled for what we currently show
      this.viewActive = false;
      this.newAlert = false;
    }
  }

  public getAlertStatus(): GLComponentAlertStatus {
    if (this.getBadgeNumber() == 0 || this.viewActive) {
      return GLComponentAlertStatus.NONE;
    }

    if (!this.viewActive && this.newAlert) {
      return GLComponentAlertStatus.ALERT_FLASH;
    }

    return GLComponentAlertStatus.ALERT;
  }

  public filterSelectionChanged(filterConfiguration: FilterConfiguration) {
    this.service.updateFilterSelection(filterConfiguration ?? []);
    this.service.updateBadgeCount(this);
    super.filterSelectionChanged(filterConfiguration);
  }

  public getFilterConfiguration(): any {
    return this.service.getFilterConfiguration();
  }

  public restoreFromConfiguration(config: any): void {
    // Filters from service configuration
    if (config != null) {
      delete config.filterConfiguration;
    }

    super.restoreFromConfiguration(config);
  }

  public getComponentConfiguration(): any {
    let configuration = super.getComponentConfiguration();

    // Filters from service configuration
    if (configuration != null) {
      delete configuration.filterConfiguration;
    }

    return configuration;
  }

  public onCellDoubleClicked(event) {
    // binding for template. please overide in subclass
    if (
      event === Object(event) &&
      event.node === Object(event.node) &&
      typeof event.node.id === "string" &&
      event.node.data === Object(event.node.data) &&
      event.node.data.orderData === Object(event.node.data.orderData) &&
      typeof event.node.data.orderData.orderId.toString() === "string" &&
      typeof event.node.data.eventType === "number"
    ) {
      if (CubiqTS.InterventionEventType.FIXING_RATE === event.node.data.eventType) {
        logger.out.info(
          `BfxInterventionBlotter: ignoring double click for Fixing Rate intervention event ${event.node.id}`,
        );
      }

      let itemState: any = {
        referencedOrderIds: [event.node.data.orderData.orderId.toString()],
      };

      this.contextMenuService.fireActionEvent(
        BfxBlotterAction.ACTION_DI_VIEW_DETAILS,
        this,
        [event.node.id],
        [event.node.data],
        itemState,
        null,
        event,
      );
    } else {
      logger.out.warn(
        `BfxInterventionBlotter: ignoring double click for intervention event ${event.node.id} with missing information`,
      );
    }
  }

  public getTabIdentifier(): string {
    return "BfxInterventionBlotterComponent";
  }
}
